import { useState, useEffect } from "react";
import { getRandomQuote } from "@/content/quotes";

/**
 * Picks a quote once per mount. Because the homepage hero remounts on every
 * navigation to "/", and the ribbon remounts on every page change, visitors
 * effectively see a new quote on each page load.
 */
const useQuote = () => {
  const [q] = useState(() => getRandomQuote());
  return q;
};

/** Subtle italic line for use under the hero headline. */
export const HeroQuote = () => {
  const q = useQuote();
  return (
    <div className="mt-8 max-w-xl">
      <div className="flex items-start gap-3">
        <span className="font-display text-gold text-2xl leading-none">"</span>
        <div>
          <p className="text-white/90 italic text-sm md:text-base leading-relaxed">
            {q.text}
          </p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold/80 mt-2">
            — Dr. Sully · {q.source}
          </p>
        </div>
      </div>
    </div>
  );
};

/** Slim global ribbon — drops in above the footer on every page. */
export const QuoteRibbon = () => {
  const [q, setQ] = useState(() => getRandomQuote());
  // Also rotate every 14s for visitors who linger on a single page.
  useEffect(() => {
    const id = setInterval(() => setQ(getRandomQuote()), 14000);
    return () => clearInterval(id);
  }, []);

  return (
    <aside
      aria-label="Quote of the moment from Dr. Sully"
      data-visual-dynamic="rotating-quote"
      className="border-y border-border bg-navy"
    >
      <div className="container py-4 flex items-center gap-4 overflow-hidden">
        <span className="hidden sm:inline-flex items-center font-display text-[10px] uppercase tracking-[0.3em] text-gold/80 flex-shrink-0">
          <span className="h-px w-6 bg-gold mr-3" />
          Built On Purpose
        </span>
        <p
          key={q.text}
          className="text-silver text-xs md:text-sm italic leading-relaxed truncate animate-[fade-in_500ms_ease-out]"
          title={`${q.text} — ${q.source}`}
        >
          "{q.text}"
          <span className="hidden md:inline text-gold not-italic font-display uppercase tracking-[0.25em] text-[10px] ml-3">
            — {q.source}
          </span>
        </p>
      </div>
    </aside>
  );
};
